import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CloudArrowUpIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Cropper from 'react-easy-crop';
import { useDropzone } from 'react-dropzone';
import { useAppDispatch } from '../../hooks/redux';
import { uploadProfilePhoto } from '../../store/slices/userSlice';
import Button from '../common/Button';
import toast from 'react-hot-toast';
import { compressImage } from '../../utils/compression';
import useBackButton from '../../hooks/useBackButton';

interface PhotoCropperModalProps {
  type: 'profile' | 'cover';
  currentPhoto?: string;
  onClose: () => void;
  photoToCrop?: string;
  onCropConfirm?: (blob: Blob) => void;
}

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

const PhotoCropperModal: React.FC<PhotoCropperModalProps> = ({ type, currentPhoto, photoToCrop, onClose, onCropConfirm }) => {
  useBackButton(onClose, true, 150);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState<'upload' | 'crop'>('upload');
  
  const dispatch = useAppDispatch();

  // Get aspect ratio based on photo type
  const aspectRatio = type === 'profile' ? 1 : 3; // 1:1 for profile, 3:1 for cover
  const cropShape = type === 'profile' ? 'round' : 'rect';

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const error = rejectedFiles[0].errors[0];
        if (error.code === 'file-invalid-type') {
          toast.error('Invalid file type. Please upload an image.');
        } else {
          toast.error('Invalid file. Please try another image.');
        }
        return;
      }

      const file = acceptedFiles[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          setImageSrc(reader.result as string);
          setStep('crop');
        };
        reader.readAsDataURL(file);
      }
    },
  });

  useEffect(() => {
    if(photoToCrop){
      setImageSrc(photoToCrop);
      setStep('crop');
    }
  },[photoToCrop])

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = useCallback(
    async (imageSrc: string, pixelCrop: Area, rotation = 0): Promise<Blob> => {
      const createImage = (url: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.addEventListener('load', () => resolve(image));
          image.addEventListener('error', error => reject(error));
          image.setAttribute('crossOrigin', 'anonymous');
          // Only append timestamp for remote URLs to avoid CORS cache issues
          if (url.startsWith('http')) {
            image.src = url + (url.includes('?') ? '&' : '?') + 't=' + new Date().getTime();
          } else {
            image.src = url;
          }
        });

      const getRadianAngle = (degreeValue: number) => {
        return (degreeValue * Math.PI) / 180;
      };

      const rotateSize = (width: number, height: number, rotation: number) => {
        const rotRad = getRadianAngle(rotation);
        return {
          width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
          height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
        };
      };

      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      const rotRad = getRadianAngle(rotation);
      const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
        image.width,
        image.height,
        rotation
      );

      canvas.width = bBoxWidth;
      canvas.height = bBoxHeight;

      ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
      ctx.rotate(rotRad);
      ctx.translate(-image.width / 2, -image.height / 2);

      ctx.drawImage(image, 0, 0);

      const croppedCanvas = document.createElement('canvas');
      const croppedCtx = croppedCanvas.getContext('2d');

      if (!croppedCtx) {
        throw new Error('No 2d context');
      }

      croppedCanvas.width = pixelCrop.width;
      croppedCanvas.height = pixelCrop.height;

      croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      return new Promise<Blob>((resolve, reject) => {
        croppedCanvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas to blob conversion failed'));
          }
        }, 'image/jpeg', 0.9);
      });
    },
    []
  );

  const handleCropAndUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      toast.error('Please select and crop an image first');
      return;
    }

    setUploading(true);
    try {
      // Get cropped image
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      
      // If onCropConfirm is provided, return the blob directly and skip upload
      if (onCropConfirm) {
        onCropConfirm(croppedImage);
        onClose();
        return;
      }

      // Compress the cropped image to ensure it respects the 2MB target
      const finalImage = await compressImage(new File([croppedImage], `${type}-photo.jpg`, { type: 'image/jpeg' }), { maxSizeMB: 0.8 });

      const formData = new FormData();
      formData.append('image', finalImage, `${type}-photo.jpg`);

      // // console.log('🚀 Starting cropped photo upload for type:', type);

      const result = await dispatch(uploadProfilePhoto({ type, file: formData }));

      if (uploadProfilePhoto.fulfilled.match(result)) {
        // // console.log('✅ Cropped photo upload successful');
        // toast.success(`${type === 'profile' ? 'Profile' : 'Cover'} photo updated successfully!`);
        onClose();
      } else {
        // console.error('❌ Cropped photo upload failed:', result.payload);
        toast.error(result.payload as string || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('❌ Photo cropping/upload error:', error);
      toast.error('Failed to process photo');
    } finally {
      setUploading(false);
    }
  };

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  const getDimensionsText = () => {
    if (type === 'profile') {
      return 'Final size: 400×400px (1:1 ratio)';
    } else {
      return 'Final size: 1200×400px (3:1 ratio)';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl flex flex-col shadow-2xl overflow-hidden max-h-[90vh] border border-gray-100 dark:border-gray-800"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 z-10">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {type === 'profile' ? 'Update Profile Photo' : 'Update Cover Photo'}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content Area */}
          <div className="flex-1 relative bg-gray-50 dark:bg-gray-950 overflow-hidden flex flex-col">
            {step === 'upload' ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div
                  {...getRootProps()}
                  className={`w-full max-w-lg border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-200 ${
                    isDragActive 
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/10 scale-[1.02]' 
                      : 'border-gray-300 dark:border-gray-700 hover:border-teal-400 dark:hover:border-teal-500 hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg'
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className="bg-teal-50 dark:bg-teal-900/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CloudArrowUpIcon className="h-10 w-10 text-teal-600 dark:text-teal-400" />
                  </div>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {isDragActive ? 'Drop photo here' : 'Click or drag to upload'}
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-xs mx-auto">
                    Support for JPG, PNG and WEBP. High resolution recommended.
                  </p>
                  <Button variant="outline" className="mx-auto pointer-events-none">
                    Select File
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex-1 relative bg-gray-900 min-h-[400px]">
                {imageSrc && (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={aspectRatio}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onRotationChange={setRotation}
                    onCropComplete={onCropComplete}
                    cropShape={cropShape as any}
                    showGrid={true}
                    objectFit="contain"
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer Controls */}
          <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 z-10">
            {step === 'crop' && (
              <div className="flex items-center gap-6 mb-6 px-2">
                <div className="flex-1">
                  <div className="flex justify-between mb-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Zoom</label>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{Math.round(zoom * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Rotation</label>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{Math.round(rotation)}°</span>
                  </div>
                  <input
                    type="range"
                    value={rotation}
                    min={-180}
                    max={180}
                    step={1}
                    onChange={(e) => setRotation(Number(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
                  />
                </div>
                <button 
                  onClick={resetCrop}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors mt-4"
                  title="Reset"
                >
                  <ArrowPathIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            {!imageSrc && (
              <div className="flex justify-center mb-4">
                <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                  {getDimensionsText()}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => step === 'crop' ? setStep('upload') : onClose()} 
                disabled={uploading}
              >
                {step === 'crop' ? 'Back' : 'Cancel'}
              </Button>
              {step === 'crop' && (
                <Button 
                  variant="primary" 
                  onClick={handleCropAndUpload} 
                  loading={uploading} 
                  disabled={!croppedAreaPixels || uploading}
                >
                  {onCropConfirm ? 'Confirm Crop' : 'Save & Upload'}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PhotoCropperModal;
